const React = require('react');
const Form = require('./useFormPrimitives/PrimitiveForm');
const FormHookUtils = require('./useFormPrimitives/formHookUtils');
const FormModelUtils = require('./useFormPrimitives/formModelUtils');
const FormSubmitUtils = require('./useFormPrimitives/formSubmitUtils');
const usePrimitiveFormController = require('./useFormPrimitives/usePrimitiveFormController');

const { useMemo } = React;

const internals = {};
internals.ORIGINAL_HANDLE_SUBMIT_KEY = FormSubmitUtils.ORIGINAL_HANDLE_SUBMIT_KEY;
internals.WRAPPED_HANDLE_SUBMIT_KEY = FormSubmitUtils.WRAPPED_HANDLE_SUBMIT_KEY;

function useForm(config, configProps) {

    const props = internals.useResolvedFormConfig(config, configProps);
    const {
        title,
        onSubmit = internals.noop,
        onInvalidSubmit,
        onSubmitError,
        fields,
        defaults,
        formId,
        formOptions = {},
        resolver,
        schema,
        syncDefaults = true
    } = props;

    if (!fields) {
        throw new Error('useForm requires fields as props.');
    }

    const controller = internals.usePrimitiveFormController({
        defaults,
        fields,
        formId,
        formOptions,
        onInvalidSubmit,
        onSubmit,
        onSubmitError,
        resolver,
        schema,
        syncDefaults
    });

    return {
        ...controller,
        Form: (
            <Form
                title={title}
                methods={controller.methods}
                fields={controller.fields}
                formConfig={controller.formConfig}
                formId={formId}
                formModel={controller.formModel}
                formProps={controller.formProps}
                onInvalidSubmit={onInvalidSubmit}
                onSubmit={onSubmit}
                onSubmitError={onSubmitError}
            />
        )
    };
}

internals.useResolvedFormConfig = (config, configProps) => {

    const rerenderDeps = internals.getRerenderDeps(config, configProps);

    return useMemo(() => internals.resolveFormConfig(config, configProps), rerenderDeps);
};

internals.resolveFormConfig = (config, configProps) => {

    if (typeof config === 'function') {
        return config(configProps || {});
    }

    if (typeof config?.buildFormConfig === 'function') {
        return config.buildFormConfig(configProps || {});
    }

    return config || {};
};

internals.getRerenderDeps = (config, configProps) => {

    if (!internals.isBuildableConfig(config)) {
        return [config];
    }

    const deps = typeof config.rerenderDeps === 'function'
        ? config.rerenderDeps(configProps || {})
        : config.rerenderDeps;

    if (Array.isArray(deps)) {
        return [config, ...deps];
    }

    return [config, ...internals.deriveRerenderDeps(configProps)];
};

internals.isBuildableConfig = (config) => typeof config === 'function'
    || typeof config?.buildFormConfig === 'function';

internals.deriveRerenderDeps = (configProps) => {

    if (!configProps || typeof configProps !== 'object') {
        return [configProps];
    }

    return Object.keys(configProps)
        .sort()
        .reduce((deps, key) => deps.concat(configProps[key]), []);
};

internals.when = (condition, value) => {

    if (!condition) {
        return null;
    }

    return typeof value === 'function' ? value() : value;
};

internals.usePrimitiveFormController = usePrimitiveFormController;
internals.buildUseFormOptions = FormHookUtils.buildUseFormOptions;
internals.buildFormModel = FormHookUtils.buildFormModel;
internals.buildFieldProviderConfig = FormHookUtils.buildFieldProviderConfig;
internals.normalizeFields = FormModelUtils.normalizeFields;
internals.normalizeField = FormModelUtils.normalizeField;
internals.sortSpecifyOptions = FormModelUtils.sortSpecifyOptions;
internals.isSpecifyOption = FormModelUtils.isSpecifyOption;
internals.getOptionSearchText = FormModelUtils.getOptionSearchText;
internals.areEquivalent = FormModelUtils.areEquivalent;
internals.stringifyDefaults = FormModelUtils.stringifyDefaults;
internals.getFieldDefault = FormModelUtils.getFieldDefault;
internals.collectFieldDefaults = FormModelUtils.collectFieldDefaults;
internals.buildDefaults = FormModelUtils.buildDefaults;
internals.compactFields = FormModelUtils.compactFields;
internals.noop = FormSubmitUtils.noop;
internals.getLatestSubmitValues = FormSubmitUtils.getLatestSubmitValues;
internals.wrapHandleSubmit = FormSubmitUtils.wrapHandleSubmit;
internals.buildSubmitHandler = FormSubmitUtils.buildSubmitHandler;

module.exports = useForm;
module.exports.useForm = useForm;
module.exports.internals = internals;
module.exports.when = internals.when;
module.exports.compactFields = internals.compactFields;
